import { useEffect } from 'react';
import classnames from 'classnames';
import { useIMask } from 'react-imask';
import { prettyDate } from '@/helpers/date';
import IconDate from './icon_calendar.svg?react';
import s from './index.module.scss';

/**
 * @param {value} - value 'yyyy-mm-dd' or null
 * @param {onChange} - (newValue: 'yyyy-mm-dd' | null) => void
 * @param {onFocusIn} - fires once input focused: () => void
 * @param {onFocusOut} - fires once input out of focus: () => void
 * @param {placeholder} - any string.
 */

export const MonthInput = ({
  value,
  onChange,
  onFocusIn,
  onFocusOut,
  placeholder = 'mm.yyyy',
  ...rest
}) => {
  const { ref, maskRef } = useIMask(
    {
      mask: '00.0000', // mm.yyyy
      lazy: true, // show full pattern while typing; remove if you want only placeholder
      autofix: true, // attempts to fix simple mistakes like "13" → "01"
    },
    {
      // We intentionally do NOT call onChange here – only on valid blur, per requirements.
    },
  );

  // Convert prop value ('yyyy-mm-dd' or null) → masked string ('mm.yyyy')
  const externalToMasked = externalValue => {
    if (!externalValue) return '';
    const [year, month] = externalValue.split('-');
    if (!year || !month) return '';
    return `${month}.${year}`;
  };

  // Convert masked string ('mm.yyyy') → external value ('yyyy-mm-01' or null)
  const maskedToExternal = maskedValue => {
    if (!maskedValue) return null;

    const [mm, yyyy] = maskedValue.split('.');
    if (!mm || !yyyy) return null;

    const month = parseInt(mm, 10);
    const year = parseInt(yyyy, 10);

    if (
      Number.isNaN(month) ||
      Number.isNaN(year) ||
      mm.length !== 2 ||
      yyyy.length !== 4 ||
      month < 1 ||
      month > 12
    ) {
      return null;
    }

    // Represent month as the first day of that month
    const iso = `${yyyy}-${String(month).padStart(2, '0')}-01`;

    // Extra safety check for a real date
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;

    return iso;
  };

  // Keep mask input in sync with external value (2‑way binding from props → input)
  useEffect(() => {
    if (!maskRef.current) return;

    const targetMasked = externalToMasked(value);
    if (maskRef.current.value !== targetMasked) {
      maskRef.current.value = targetMasked;
    }
  }, [value, maskRef]);

  const handleFocus = e => {
    if (onFocusIn) onFocusIn(e);
  };

  const handleBlur = e => {
    if (!maskRef.current) {
      if (onFocusOut) onFocusOut(e);
      return;
    }

    const currentMasked = maskRef.current.value;
    const externalValue = maskedToExternal(currentMasked);

    if (externalValue) {
      // Valid date → notify parent
      if (onChange) onChange(externalValue);
    } else {
      // Invalid or incomplete date → clear input and notify parent with null
      maskRef.current.value = '';
      if (onChange) onChange(null);
    }

    if (onFocusOut) onFocusOut(e);
  };

  return (
    <div className={s.wrapper}>
      <div className={s.input_wrapper}>
        <input
          ref={ref}
          className={classnames(s.input, value && s.input_filled)}
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...rest}
        />
        {value && <span className={s.date}>{prettyDate(value, 'MMM YYYY')}</span>}
        <IconDate className={classnames(s.icon, value && s.icon_active)} />
      </div>
    </div>
  );
};
